/**
 * GraphCanvas - ReactFlow graph with node menu
 *
 * Self-contained component using hooks and context for state.
 */

import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import {
  Connection, Controls, Edge, EdgeChange, MiniMap, NodeChange,
  ReactFlow, applyNodeChanges
} from '@xyflow/react';

import { NodeMenu } from '../NodeMenu';
import { GraphNode } from '../node/GraphNode';
import { StructuralNode } from '../node/StructuralNode';
import { MutableEdge } from './MutableEdge';
import { FloatingEdge, FloatingConnectionLine } from './FloatingEdge';
import { GradientField } from './fields/GradientField';

import { Performance2DCanvas } from '../renderers/Performance2DCanvas';
import { useScope } from '../../contexts/ScopeContext';
import { useAppState } from '../../contexts/AppContext';
import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { webRuntimeApi } from '@/runtime/web';
import { toReactFlowEdges, toReactFlowNodes } from './util';
import { TYPE_COLORS, getEndpointColor, areTypesCompatible, GRAPH_BG } from '@/ui/shared/utils';
import { Performance3DCanvas } from '../renderers/Performance3DCanvas';



// Grid and origin axes - renders aligned grid lines and axis lines at origin
function OriginAxes({ viewport, containerRef }: {
  viewport: { x: number; y: number; zoom: number };
  containerRef: React.RefObject<HTMLDivElement>;
}) {
  const extent = 10000;
  const gap = 20;

  // Calculate visible grid range based on viewport
  const container = containerRef.current;
  const width = container?.clientWidth ?? 1000;
  const height = container?.clientHeight ?? 800;

  // Convert screen bounds to flow coordinates
  const minX = Math.floor((-viewport.x / viewport.zoom - gap) / gap) * gap;
  const maxX = Math.ceil(((-viewport.x + width) / viewport.zoom + gap) / gap) * gap;
  const minY = Math.floor((-viewport.y / viewport.zoom - gap) / gap) * gap;
  const maxY = Math.ceil(((-viewport.y + height) / viewport.zoom + gap) / gap) * gap;

  // Generate grid lines aligned to origin
  const gridLines: JSX.Element[] = [];

  // Vertical lines
  for (let x = minX; x <= maxX; x += gap) {
    gridLines.push(
      <line
        key={`v${x}`}
        x1={x}
        y1={minY}
        x2={x}
        y2={maxY}
        stroke="#1e252e"
        strokeWidth={1 / viewport.zoom}
      />
    );
  }

  // Horizontal lines
  for (let y = minY; y <= maxY; y += gap) {
    gridLines.push(
      <line
        key={`h${y}`}
        x1={minX}
        y1={y}
        x2={maxX}
        y2={y}
        stroke="#1e252e"
        strokeWidth={1 / viewport.zoom}
      />
    );
  }

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'visible'
      }}
    >
      <g
        transform={`translate(${viewport.x}, ${viewport.y}) scale(${viewport.zoom})`}
      >
        {/* Grid lines */}
        {gridLines}
        {/* X axis (horizontal line at y=0) */}
        <line
          x1={-extent}
          y1={0}
          x2={extent}
          y2={0}
          stroke="#2a3441"
          strokeWidth={1 / viewport.zoom}
        />
        {/* Y axis (vertical line at x=0) */}
        <line
          x1={0}
          y1={-extent}
          x2={0}
          y2={extent}
          stroke="#2a3441"
          strokeWidth={1 / viewport.zoom}
        />
        {/* Origin marker */}
        <circle
          cx={0}
          cy={0}
          r={3 / viewport.zoom}
          fill="#343f4d"
        />
      </g>
    </svg>
  );
}

// Custom node types
const nodeTypes = {
  graphNode: GraphNode,
  constraintNode: StructuralNode,
  gradientField: GradientField
};

// Edge styling - use MutableEdge for mute button on hover
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const edgeTypes: any = {
  mutable: MutableEdge,
  floating: FloatingEdge,
};
const defaultEdgeOptions = {
  type: 'mutable'
};


export function GraphCanvas() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isNodeMenuOpen, setIsNodeMenuOpen] = useState(false);

  const { instanceId, definitionId, definitionState, instanceState, selectedNodeIds, setSelectedNodeIds } = useScope();
  const { modifierKeyPressed, projectName, logTrace, logError } = useAppState();
  const defaultView = webRuntimeApi.getDefaultView();

  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [currentViewport, setCurrentViewport] = useState({ x: 0, y: 0, zoom: 1 });
  const [connectionLineColor, setConnectionLineColor] = useState(TYPE_COLORS.any);
  const [fieldNodes, setFieldNodes] = useState<any[]>([]);
  const fieldIdCounter = useRef(0);

  const onResizeField = useCallback((id: string, width: number, height: number) => {
    setFieldNodes(prev => prev.map(f =>
      f.id === id ? { ...f, data: { ...f.data, width, height } } : f
    ));
  }, []);

  // On init, apply viewport from state or fitView
  const onInit = useCallback((instance: any) => {
    setReactFlowInstance(instance);

    if (instanceId && !instanceState?.viewport) {
      const hasNodes = definitionState.nodes && Object.keys(definitionState.nodes).length > 0;

      if (hasNodes) {
        instance.fitView({ padding: 1.0, maxZoom: 0.8 });
      } else {
        const container = wrapperRef.current;
        if (container) {
          const centered = {
            x: container.clientWidth / 2,
            y: container.clientHeight / 2,
            zoom: 1
          };
          instance.setViewport(centered);
          setCurrentViewport(centered);
        }
      }
    }
  }, [instanceId, projectName]);



  const { addConnection, addNode, removeConnection, setNodePosition, setViewport } = useRuntime();

  // Base graph nodes from definition state
  const baseGraphNodes = useMemo(
    () => definitionState
      ? toReactFlowNodes(definitionState, instanceId)
      : [],
    [definitionState?.nodes, definitionState?.positions, instanceId]
  );

  // Merge graph nodes + field nodes
  const allBaseNodes = useMemo(
    () => [...fieldNodes, ...baseGraphNodes],
    [fieldNodes, baseGraphNodes]
  );

  // Local nodes state for smooth dragging - syncs with base when not dragging
  const [nodes, setNodes] = useState(allBaseNodes);

  useEffect(() => {
    setNodes(allBaseNodes);
  }, [allBaseNodes]);

  const edges = useMemo(
    () => {
      const base = definitionState ? toReactFlowEdges(definitionState) : [];
      if (defaultView === 'graph') return base;
      // Hide edges visually but keep them in ReactFlow's internal state
      return base.map(e => ({ ...e, hidden: true }));
    },
    [definitionState?.nodes, definitionState?.connections, instanceId, defaultView]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onMove = useCallback((_event: any, viewport: { x: number; y: number; zoom: number }) => {
    setCurrentViewport(viewport);
  }, []);

  const onMoveEnd = useCallback((_event: any, viewport: { x: number; y: number; zoom: number }) => {
    setCurrentViewport(viewport);
    setViewport(instanceId, viewport);
  }, [instanceId, setViewport]);

  const onNodeDrag = useCallback((_event: any, _node: any) => {
    // No-op: spatial audio for 2D is handled by Polyline2DRenderer
  }, []);

  const onNodeDragStop = useCallback((_event: any, node: any) => {
    if (node.type === 'gradientField') {
      setFieldNodes(prev => prev.map(f =>
        f.id === node.id ? { ...f, position: { ...node.position } } : f
      ));
    } else {
      setNodePosition(definitionId, node.id, node.position.x, node.position.y);
    }
  }, [definitionId, setNodePosition]);


  const isValidConnection = useCallback((connection: Edge | Connection) => {
    if (!definitionState || !connection.source || !connection.target
        || !connection.sourceHandle || !connection.targetHandle) return false;

    const sourceNode = definitionState.nodes[connection.source];
    const targetNode = definitionState.nodes[connection.target];
    const sourceEp = sourceNode?.endpoints[connection.sourceHandle];
    const targetEp = targetNode?.endpoints[connection.targetHandle];
    if (!sourceEp || !targetEp) return false;

    return areTypesCompatible(sourceEp.dataType, targetEp.accepts);
  }, [definitionState]);

  const onConnect = useCallback((connection: Connection) => {
    const { source, target, sourceHandle, targetHandle } = connection;
    logTrace('GraphCanvas', `onConnect: ${source}.${sourceHandle} -> ${target}.${targetHandle} (instanceId: ${instanceId})`);
    if (!source || !target || !sourceHandle || !targetHandle) {
      logError('GraphCanvas', `Invalid connection: missing ${!source ? 'source' : !target ? 'target' : !sourceHandle ? 'sourceHandle' : 'targetHandle'}`);
      return;
    }

    try {
      addConnection(instanceId, `${source}.${sourceHandle}`, `${target}.${targetHandle}`);
      logTrace('GraphCanvas', 'addConnection succeeded');
    } catch (err) {
      logError('GraphCanvas', `Failed to add connection: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [instanceId, addConnection, logTrace, logError]);

  // Track connection line color from source endpoint
  const onConnectStart = useCallback((_: any, params: { nodeId: string | null; handleId: string | null }) => {
    if (params.nodeId && params.handleId && definitionState) {
      const node = definitionState.nodes[params.nodeId];
      const endpoint = node?.endpoints[params.handleId];
      const color = getEndpointColor(endpoint?.dataType, endpoint?.dataFlow);
      setConnectionLineColor(color);
    }
  }, [definitionState]);

  const onConnectEnd = useCallback(() => {
    setConnectionLineColor(TYPE_COLORS.any);
  }, []);

  // Handle edge delete
  const onEdgesDelete = useCallback((edgesToDelete: Edge[]) => {
    for (const edge of edgesToDelete) {
      const [source, target] = edge.id.split('->');
      if (!source || !target) {
        logError('GraphCanvas', `Invalid edge id format: ${edge.id}`);
        continue;
      }
      removeConnection(instanceId, source, target);
    }
  }, [instanceId, removeConnection, logError]);

  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    const bounds = wrapperRef.current?.getBoundingClientRect();
    if (!bounds) return;

    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top
    });

    // Check if this is a field drop
    const fieldType = event.dataTransfer.getData('application/field-type');
    if (fieldType) {
      const id = `field_${Date.now()}_${fieldIdCounter.current++}`;
      const defaultWidth = 300;
      const defaultHeight = 200;
      setFieldNodes(prev => [...prev, {
        id,
        type: 'gradientField',
        position: { x: position.x - defaultWidth / 2, y: position.y - defaultHeight / 2 },
        draggable: true,
        zIndex: -1,
        data: {
          fieldType: 'gradient',
          width: defaultWidth,
          height: defaultHeight,
          axis: 'x' as const,
          label: fieldType,
          color: '#3b82f6',
          onResize: onResizeField
        }
      }]);
      return;
    }

    // Otherwise it's a node drop
    const nodeType = event.dataTransfer.getData('application/reactflow-type');
    if (nodeType) {
      addNode(instanceId, nodeType, position.x, position.y);
    }
  }, [instanceId, wrapperRef, reactFlowInstance]);


  const onNodesChange = useCallback((changes: NodeChange[]) => {
    // Apply all changes locally for smooth dragging (positions sync to store on dragStop)
    setNodes(nds => applyNodeChanges(changes, nds) as typeof nds);

    // Handle selection changes
    let selectionChanged = false;
    const newSelection = new Set(selectedNodeIds);

    for (const change of changes) {
      if (change.type === 'select') {
        selectionChanged = true;
        if (change.selected) {
          newSelection.add(change.id);
        } else {
          newSelection.delete(change.id);
        }
      }
    }

    if (selectionChanged) {
      setSelectedNodeIds(Array.from(newSelection));
    }
  }, [selectedNodeIds, setSelectedNodeIds]);


  const onEdgesChange = useCallback((_changes: EdgeChange[]) => {
    // Edge changes are handled by onConnect and removeConnection
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeIds([]);
  }, [setSelectedNodeIds]);



  return (
    <div style={styles.canvas} ref={wrapperRef}>

      {defaultView === '3d' ? (
        <Performance3DCanvas />
      ) : defaultView === '2d' ? (
        <Performance2DCanvas />
      ) : (
        <ReactFlow
          key={`${instanceId}-${projectName}`}
          nodes={nodes}
          edges={edges}
          onInit={onInit}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onMove={onMove}
          onMoveEnd={onMoveEnd}
          isValidConnection={isValidConnection}
          onConnect={onConnect}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          connectionLineStyle={{ stroke: connectionLineColor, strokeWidth: 2 }}
          connectionLineComponent={FloatingConnectionLine}
          onEdgesDelete={onEdgesDelete}
          onPaneClick={onPaneClick}
          onEdgesChange={onEdgesChange}
          onNodesChange={onNodesChange}
          onDragOver={onDragOver}
          onDrop={onDrop}
          panOnDrag={!modifierKeyPressed}
          selectionOnDrag={modifierKeyPressed}
          selectNodesOnDrag={false}
          elementsSelectable={modifierKeyPressed}
          multiSelectionKeyCode={['Meta', 'Control']}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          deleteKeyCode={['Backspace', 'Delete']}
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={instanceState?.viewport ?? { x: 0, y: 0, zoom: 0.9 }}
        >
          <OriginAxes viewport={currentViewport} containerRef={wrapperRef} />
          <Controls />
          <MiniMap
            zoomable
            pannable
            nodeColor="#fff"
            nodeStrokeColor="#fff"
            nodeStrokeWidth={1}
            maskColor="rgba(0, 0, 0, 0.5)"
            style={{ background: '#1e293b' }}
          />
        </ReactFlow>
      )}

      <NodeMenu
        isOpen={isNodeMenuOpen}
        onToggle={() => setIsNodeMenuOpen(!isNodeMenuOpen)}
      />


    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  canvas: {
    flex: 1,
    background: GRAPH_BG,
  }
};
